import { Link } from 'react-router-dom';
import { ReactNode } from 'react';

interface RelatedPost {
  title: string;
  slug: string;
  category: string;
  date: string;
  readTime: string;
  excerpt: string;
  image: string;
}

interface ArticleLayoutProps {
  category: string;
  title: string;
  date: string;
  readTime: string;
  heroImage: string;
  heroImageAlt: string;
  children: ReactNode;
  relatedPosts: RelatedPost[];
}

export default function ArticleLayout({
  category,
  title,
  date,
  readTime,
  heroImage,
  heroImageAlt,
  children,
  relatedPosts,
}: ArticleLayoutProps) {
  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight text-gray-900 cursor-pointer">
            Trewaz
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            <Link to="/#features" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Features</Link>
            <Link to="/#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">How It Works</Link>
            <Link to="/#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Pricing</Link>
            <Link to="/blog" className="text-sm font-medium text-gray-900 transition-colors cursor-pointer">Blog</Link>
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Login</Link>
            <Link
              to="/register"
              className="text-sm font-medium bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </header>

      {/* Back to blog */}
      <div className="max-w-3xl mx-auto px-6 pt-10">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Back to Blog
        </Link>
      </div>

      {/* Article Header */}
      <div className="max-w-3xl mx-auto px-6 pt-8 pb-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs font-medium bg-gradient-to-r from-teal-500 to-indigo-500 bg-clip-text text-transparent border border-teal-200 rounded-full px-3 py-0.5">{category}</span>
          <span className="text-xs text-gray-400">{readTime}</span>
          <span className="text-xs text-gray-400">{date}</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight tracking-tight mb-6">{title}</h1>

        {/* Author strip */}
        <div className="flex items-center gap-3 pb-8 border-b border-gray-100">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-indigo-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">T</div>
          <div>
            <p className="text-sm font-medium text-gray-900">Trewaz Editorial Team</p>
            <p className="text-xs text-gray-500">AI & Travel Automation Specialists</p>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="max-w-4xl mx-auto px-6 mb-12">
        <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden bg-gray-100">
          <img src={heroImage} alt={heroImageAlt} className="w-full h-full object-cover object-top" />
        </div>
      </div>

      {/* Article Body */}
      <article className="max-w-3xl mx-auto px-6 pb-16">
        <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed">
          {children}
        </div>
      </article>

      {/* CTA Banner */}
      <div className="max-w-4xl mx-auto px-6 mb-20">
        <div className="rounded-2xl bg-gray-900 p-10 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Ready to Automate Your Travel Agency?</h3>
          <p className="text-gray-400 text-sm mb-7 max-w-xl mx-auto">Join hundreds of travel agencies already using Trewaz to handle enquiries, WhatsApp, CRM, and bookings — all on autopilot.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="bg-white text-gray-900 text-sm font-semibold px-6 py-3 rounded-md hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Start Free Trial
            </Link>
            <Link
              to="/register?plan=growth"
              className="border border-gray-600 text-white text-sm font-medium px-6 py-3 rounded-md hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              Book a Demo
            </Link>
          </div>
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="max-w-4xl mx-auto px-6 pb-20">
          <h4 className="text-lg font-semibold text-gray-900 mb-6">Related Articles</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {relatedPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group border border-gray-100 rounded-xl overflow-hidden hover:border-gray-200 transition-all cursor-pointer block"
              >
                <div className="w-full h-40 bg-gray-100 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{post.category}</span>
                    <span className="text-xs text-gray-400">{post.readTime}</span>
                  </div>
                  <h5 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-teal-600 transition-colors">{post.title}</h5>
                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2">{post.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm font-bold text-gray-900">Trewaz</span>
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} Trewaz. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/blog" className="text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Blog</Link>
            <a href="#" className="text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Privacy Policy</a>
            <a href="#" className="text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
